// Baked ground occlusion: once, at start-up, render a top-down height map of the static Ministry and
// turn it into an ambient-occlusion texture that the floors sample by world XZ. Walls, furniture and
// props get soft contact shadows where they meet the ground — the depth cue a toon ramp and one sun
// can't give — for one texture read per floor pixel and no per-frame work.
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

export interface GroundBounds {
  x: readonly [number, number];
  z: readonly [number, number];
}

const MAX_H = 8;
/** Dev: `?aodebug` paints the occlusion itself onto the floors. */
const AO_DEBUG = import.meta.env.DEV && new URLSearchParams(location.search).has('aodebug');

const heightMaterial = new THREE.ShaderMaterial({
  vertexShader: /* glsl */ `
    varying float vY;
    void main() {
      vec4 w = modelMatrix * vec4(position, 1.0);
      vY = w.y;
      gl_Position = projectionMatrix * viewMatrix * w;
    }`,
  fragmentShader: /* glsl */ `
    varying float vY;
    void main() { gl_FragColor = vec4(clamp(vY / ${MAX_H.toFixed(1)}, 0.0, 1.0), 0.0, 0.0, 1.0); }`,
  side: THREE.DoubleSide,
});

const aoShader = {
  uniforms: {
    tHeight: { value: null as THREE.Texture | null },
    texel: { value: new THREE.Vector2() },
    metre: { value: 1 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tHeight;
    uniform vec2 texel;
    uniform float metre; // texels per metre
    varying vec2 vUv;
    float h(vec2 uv) { return texture2D(tHeight, uv).r * ${MAX_H.toFixed(1)}; }
    void main() {
      float hc = h(vUv);
      // Horizon-based: in each of 16 directions, find how high the surroundings rise above this
      // point (as the sine of the elevation angle), fading with distance out to 2.6 m.
      float occ = 0.0;
      for (int i = 0; i < 16; i++) {
        float a = float(i) * 0.3926991 + 0.2;
        vec2 dir = vec2(cos(a), sin(a));
        float horizon = 0.0;
        for (int k = 0; k < 7; k++) {
          float d = k == 0 ? 0.1 : k == 1 ? 0.25 : k == 2 ? 0.45 : k == 3 ? 0.75 : k == 4 ? 1.15 : k == 5 ? 1.7 : 2.4;
          float rise = h(vUv + dir * d * metre * texel) - hc - 0.02;
          float s = rise / sqrt(rise * rise + d * d); // sin(elevation)
          horizon = max(horizon, s * (1.0 - d / 2.6));
        }
        occ += horizon;
      }
      float ao = 1.0 - 2.3 * occ / 16.0;
      gl_FragColor = vec4(vec3(clamp(ao, 0.3, 1.0)), 1.0);
    }`,
};

/**
 * Render the height map of `root` over `bounds` and bake it into an occlusion texture. Only meshes
 * `occludes` accepts are in the height map: flat things lying on the ground (rugs, paving, decals)
 * are a few centimetres "tall" to it and would ring the floor with faint outlines of themselves.
 */
export function bakeGroundAO(renderer: THREE.WebGLRenderer, root: THREE.Object3D, bounds: GroundBounds, occludes: (m: THREE.Mesh) => boolean = () => true, texelsPerMetre = 28): THREE.Texture {
  const w = bounds.x[1] - bounds.x[0];
  const d = bounds.z[1] - bounds.z[0];
  const W = Math.round(w * texelsPerMetre);
  const H = Math.round(d * texelsPerMetre);
  const cam = new THREE.OrthographicCamera(-w / 2, w / 2, d / 2, -d / 2, 0.1, 100);
  const cx = (bounds.x[0] + bounds.x[1]) / 2;
  const cz = (bounds.z[0] + bounds.z[1]) / 2;
  cam.position.set(cx, 60, cz);
  cam.up.set(0, 0, -1); // image top = the back of the board (z0)
  cam.lookAt(cx, 0, cz);
  cam.updateMatrixWorld();

  const heightRT = new THREE.WebGLRenderTarget(W, H, { type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: true });
  const aoRT = new THREE.WebGLRenderTarget(W, H, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false });

  // Render the statics alone with the height material (borrowing them into a temporary scene).
  const tmp = new THREE.Scene();
  tmp.overrideMaterial = heightMaterial;
  tmp.background = new THREE.Color(0, 0, 0);
  const parent = root.parent;
  const skipped: THREE.Object3D[] = [];
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && o.visible && !occludes(o as THREE.Mesh)) skipped.push(o);
  });
  for (const o of skipped) o.visible = false;
  tmp.add(root);
  const prevTarget = renderer.getRenderTarget();
  const prevShadow = renderer.shadowMap.enabled;
  renderer.shadowMap.enabled = false;
  renderer.setRenderTarget(heightRT);
  renderer.clear();
  renderer.render(tmp, cam);
  parent?.add(root);
  for (const o of skipped) o.visible = true;

  const mat = new THREE.ShaderMaterial(aoShader);
  mat.uniforms.tHeight!.value = heightRT.texture;
  mat.uniforms.texel!.value.set(1 / W, 1 / H);
  mat.uniforms.metre!.value = texelsPerMetre;
  const quad = new FullScreenQuad(mat);
  renderer.setRenderTarget(aoRT);
  quad.render(renderer);
  renderer.setRenderTarget(prevTarget);
  renderer.shadowMap.enabled = prevShadow;

  quad.dispose();
  mat.dispose();
  heightRT.dispose();
  return aoRT.texture;
}

/**
 * Make a floor material darken by the baked occlusion. `bounds` must match the bake; UVs are
 * derived from world XZ so any mesh can use it.
 */
export function applyGroundAO(mat: THREE.Material, ao: THREE.Texture, bounds: GroundBounds, strength = 1): void {
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, r) => {
    prev?.call(mat, shader, r);
    shader.uniforms.tGroundAO = { value: ao };
    shader.uniforms.aoBounds = { value: new THREE.Vector4(bounds.x[0], bounds.z[0], bounds.x[1] - bounds.x[0], bounds.z[1] - bounds.z[0]) };
    shader.uniforms.aoStrength = { value: strength };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vGroundXZ;')
      .replace('#include <project_vertex>', '#include <project_vertex>\nvGroundXZ = (modelMatrix * vec4(transformed, 1.0)).xz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform sampler2D tGroundAO;\nuniform vec4 aoBounds;\nuniform float aoStrength;\nvarying vec2 vGroundXZ;')
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        {
          vec2 aoUv = vec2((vGroundXZ.x - aoBounds.x) / aoBounds.z, 1.0 - (vGroundXZ.y - aoBounds.y) / aoBounds.w);
          float gao = texture2D(tGroundAO, aoUv).r;
          diffuseColor.rgb *= mix(1.0, gao, aoStrength);
          ${AO_DEBUG ? 'diffuseColor.rgb = vec3(gao);' : ''}
        }`,
      );
  };
  const key = mat.customProgramCacheKey?.bind(mat);
  mat.customProgramCacheKey = () => `${key ? key() : ''}groundAO`;
  mat.needsUpdate = true;
}
